import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import Modal from "@/components/Modal";
import { Plus, Edit2, Trash2, UserCog, ShieldCheck, ToggleLeft, ToggleRight } from "lucide-react";
import { toast } from "sonner";

// New users start locked down; the admin opts them in.
const EMPTY = {
  name: "", email: "", phone: "", role: "sales", commission_rate: 0.05,
  customers_access: "hide", suppliers_access: "hide",
};

// Visibility levels an admin can grant a sales person, per resource (most → least access).
const ACCESS_OPTIONS = [
  { value: "all", label: "All records" },
  { value: "assigned_unassigned", label: "Assigned + unassigned" },
  { value: "assigned", label: "Only assigned to them" },
  { value: "add_only", label: "Only add (can't view list)" },
  { value: "hide", label: "Hide (no access)" },
];
// Users with no stored level (new, or predating this feature) are treated as Hide.
const accessLabel = (v) => ACCESS_OPTIONS.find((o) => o.value === v)?.label || "Hide (no access)";

export default function Users() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // 'add' | 'edit'
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/users", { params: { include_inactive: true } }).then((r) => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(u.is_active ? `${u.name} disabled` : `${u.name} enabled`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update");
    }
  };

  const openAdd = () => { setForm(EMPTY); setEditing(null); setModal("add"); };
  const openEdit = (u) => {
    setForm({
      name: u.name, email: u.email, phone: u.phone || "", role: u.role || "sales",
      commission_rate: u.commission_rate ?? 0.05,
      customers_access: u.customers_access || "hide",
      suppliers_access: u.suppliers_access || "hide",
    });
    setEditing(u); setModal("edit");
  };

  const save = async () => {
    if (!form.name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    const body = {
      name: form.name, email: form.email, phone: form.phone,
      role: form.role, commission_rate: Number(form.commission_rate) || 0,
      customers_access: form.customers_access, suppliers_access: form.suppliers_access,
    };
    try {
      if (editing) {
        await api.patch(`/users/${editing.id}`, body);
      } else {
        await api.post("/users", body);
      }
      toast.success(editing ? "User updated" : "User added");
      setModal(null); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try { await api.delete(`/users/${u.id}`); toast.success("Removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div data-testid="users-page">
      <div className="flex justify-end mb-4">
        <button onClick={openAdd} className="btn-primary" data-testid="add-user-btn"><Plus size={15} /> Add User</button>
      </div>
      <div className="card-ledger overflow-hidden">
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "var(--surface-2)" }}>
            {["Name", "Email", "Phone", "Role", "Commission", "Access", "Status", ""].map((h, i) => (
              <th key={i} className="overline" style={{ fontSize: 10, padding: "12px 16px", textAlign: "left", borderBottom: "1px solid var(--border)" }}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} style={{ borderBottom: "1px solid var(--border)", opacity: u.is_active === false ? 0.5 : 1 }} data-testid={`user-row-${u.id}`}>
                <td style={{ padding: "14px 16px", fontWeight: 600, fontSize: 14 }}>
                  <div className="flex items-center gap-2">
                    <span style={{ width: 28, height: 28, borderRadius: 6, background: u.color || "var(--primary)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 12 }}>
                      {(u.name || "?").slice(0, 1).toUpperCase()}
                    </span>
                    {u.name}
                  </div>
                </td>
                <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }}>{u.email}</td>
                <td style={{ padding: "14px 16px", fontSize: 13, color: "var(--text-2)" }} className="font-mono-data">{u.phone || "—"}</td>
                <td style={{ padding: "14px 16px" }}>
                  <span className="tag" style={{ background: u.role === "admin" ? "var(--primary-light)" : "var(--surface-2)", borderColor: "var(--border)", color: u.role === "admin" ? "var(--primary)" : "var(--text-2)" }}>
                    {u.role === "admin" ? <ShieldCheck size={11} /> : <UserCog size={11} />} {u.role}
                  </span>
                </td>
                <td style={{ padding: "14px 16px", fontSize: 13 }} className="font-mono-data">{Math.round((u.commission_rate || 0) * 100)}%</td>
                <td style={{ padding: "14px 16px", fontSize: 12, color: "var(--text-2)" }} data-testid={`user-access-${u.id}`}>
                  {u.role === "admin" ? (
                    <span>Full access</span>
                  ) : (
                    <div style={{ lineHeight: 1.5 }}>
                      <div><span style={{ color: "var(--text-2)" }}>Customers:</span> {accessLabel(u.customers_access || "hide")}</div>
                      <div><span style={{ color: "var(--text-2)" }}>Suppliers:</span> {accessLabel(u.suppliers_access || "hide")}</div>
                    </div>
                  )}
                </td>
                <td style={{ padding: "14px 16px" }}>
                  <button
                    onClick={() => toggleActive(u)}
                    className="btn-ghost"
                    style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 12, color: u.is_active === false ? "var(--text-2)" : "var(--primary)" }}
                    title={u.is_active === false ? "Enable login" : "Disable login"}
                    data-testid={`user-toggle-${u.id}`}
                  >
                    {u.is_active === false ? <ToggleLeft size={18} /> : <ToggleRight size={18} />}
                    {u.is_active === false ? "Disabled" : "Active"}
                  </button>
                </td>
                <td style={{ padding: "14px 16px", textAlign: "right" }}>
                  <div className="flex gap-1 justify-end">
                    <button onClick={() => openEdit(u)} className="btn-ghost" title="Edit"><Edit2 size={14} /></button>
                    <button onClick={() => remove(u)} className="btn-ghost" style={{ color: "var(--danger)" }} title="Delete"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <Modal title={editing ? `Edit · ${editing.name}` : "Add User"} onClose={() => setModal(null)} size="md">
          <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
            <div style={{ gridColumn: "1 / -1" }}>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Name <span style={{ color: "var(--primary)" }}>*</span></label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-base" data-testid="user-name" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Email <span style={{ color: "var(--primary)" }}>*</span></label>
              <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-base" data-testid="user-email" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Phone</label>
              <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-base" data-testid="user-phone" />
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Role</label>
              <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="input-base" data-testid="user-role">
                <option value="sales">Sales Person</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Commission %</label>
              <input type="number" min={0} max={100} value={Math.round((form.commission_rate || 0) * 100)} onChange={(e) => setForm({ ...form, commission_rate: (Number(e.target.value) || 0) / 100 })} className="input-base no-spin" data-testid="user-commission" />
            </div>
            {form.role === "sales" && (
              <div style={{ gridColumn: "1 / -1", borderTop: "1px solid var(--border)", paddingTop: 14, marginTop: 2 }} data-testid="user-access-section">
                <div className="overline" style={{ fontSize: 10, marginBottom: 4 }}>Authorization</div>
                <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 12 }}>What this sales person can see. Admins always see everything.</div>
                <div className="grid gap-4" style={{ gridTemplateColumns: "1fr 1fr" }}>
                  <div>
                    <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Customers</label>
                    <select value={form.customers_access} onChange={(e) => setForm({ ...form, customers_access: e.target.value })} className="input-base" data-testid="user-customers-access">
                      {ACCESS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Suppliers</label>
                    <select value={form.suppliers_access} onChange={(e) => setForm({ ...form, suppliers_access: e.target.value })} className="input-base" data-testid="user-suppliers-access">
                      {ACCESS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                </div>
              </div>
            )}
            <div className="flex gap-3 justify-end mt-2" style={{ gridColumn: "1 / -1" }}>
              <button onClick={() => setModal(null)} className="btn-outline">Cancel</button>
              <button onClick={save} className="btn-primary" data-testid="user-save">Save</button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
